import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAllDocuments, type TenantDocument } from '../lib/hooks';
import { Card, EmptyState, PageHeader, Pill, Skeleton, cn } from '../components/ui';
import { docTone } from '../components/compliance';
import { DocumentPreviewDialog } from '../components/DocumentPreview';
import { api } from '../lib/api';
import { useToast } from '../lib/use-toast';
import { filterDocuments } from '../lib/document-filter';
import { ChevronLeft, ChevronRight, Download, Eye, FileText, Search, TriangleAlert } from 'lucide-react';

const PAGE_SIZE = 20;

export default function Documentos() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { data: allDocs, isLoading, isError } = useAllDocuments();
  const [onlyExpiring, setOnlyExpiring] = useState(searchParams.get('filter') === 'por-vencer');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [previewing, setPreviewing] = useState<TenantDocument | null>(null);

  async function handleDownload(d: TenantDocument, e: MouseEvent) {
    e.stopPropagation();
    try {
      const { url } = await api.getDocumentDownloadUrl(d.expId, d.id);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast({ message: 'No se pudo descargar el documento', kind: 'error' });
    }
  }

  const docs = useMemo(() => {
    return filterDocuments(allDocs ?? [], { onlyExpiring, query: q });
  }, [allDocs, onlyExpiring, q]);

  const totalPages = Math.max(1, Math.ceil(docs.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageDocs = docs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  function handleFilterChange(k: boolean) {
    setOnlyExpiring(k);
    setPage(0);
  }

  function handleQueryChange(value: string) {
    setQ(value);
    setPage(0);
  }

  return (
    <div>
      <PageHeader
        title="Documentos"
        subtitle="Catálogo documental del tenant · seguimiento de vigencia"
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto sm:min-w-[240px] sm:flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
            strokeWidth={1.75}
          />
          <input
            value={q}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={t('documentCatalog.searchPlaceholder')}
            aria-label={t('documentCatalog.searchPlaceholder')}
            className="w-full rounded-xl border border-line-strong bg-panel py-2 pl-9 pr-3 text-sm text-ink outline-none focus:border-primary"
          />
        </div>
        {[
          { k: false, label: 'Todos' },
          { k: true, label: 'Por vencer / vencidos' },
        ].map((f) => (
          <button
            key={String(f.k)}
            onClick={() => handleFilterChange(f.k)}
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
              onlyExpiring === f.k
                ? 'border-side bg-side text-white'
                : 'border-line-strong bg-panel text-ink-soft hover:border-ink-faint'
            )}
          >
            {f.label}
          </button>
        ))}
        {allDocs !== undefined && (
          <span className="ml-auto font-mono text-[11px] text-ink-faint">
            {docs.length} {docs.length === 1 ? 'documento' : 'documentos'}
            {allDocs.length > PAGE_SIZE && ' · cargado desde todos los expedientes'}
          </span>
        )}
      </div>

      {isLoading ? (
        <Card>
          <div className="grid gap-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudieron cargar los documentos"
            hint="Verifica tu conexión e inténtalo de nuevo."
          />
        </Card>
      ) : docs.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText className="h-8 w-8" strokeWidth={1.5} />}
            title="Sin documentos"
            hint={
              q.trim() !== ''
                ? t('documentCatalog.noResults')
                : onlyExpiring
                  ? 'Ningún documento está por vencer o vencido.'
                  : 'Los documentos cargados en los expedientes aparecerán aquí.'
            }
          />
        </Card>
      ) : (
        <div data-tour="docs-table">
          {/* mobile: stacked cards (no horizontal scroll) */}
          <div className="grid gap-2.5 sm:hidden">
            {pageDocs.map((d, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/revision/${d.expId}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/revision/${d.expId}`);
                  if (e.key === ' ') {
                    e.preventDefault();
                    navigate(`/revision/${d.expId}`);
                  }
                }}
                className="w-full rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-colors hover:border-ink-faint"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-bold text-ink">{d.name}</span>
                  <Pill tone={docTone(d.estado)} className="shrink-0">
                    {d.estado}
                  </Pill>
                </div>
                <div className="mt-1 text-sm text-ink-soft">{d.cliente}</div>
                <div className="mt-1 font-mono text-[11px] text-ink-faint">
                  {d.category}
                  {d.expiry ? ` · vence ${d.expiry}` : ''}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewing(d);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-line-strong bg-canvas px-2.5 py-1.5 text-xs font-semibold text-ink-soft hover:border-ink-faint"
                  >
                    <Eye className="h-3.5 w-3.5" strokeWidth={2} /> Ver
                  </button>
                  <button
                    onClick={(e) => void handleDownload(d, e)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-line-strong bg-canvas px-2.5 py-1.5 text-xs font-semibold text-ink-soft hover:border-ink-faint"
                  >
                    <Download className="h-3.5 w-3.5" strokeWidth={2} /> Descargar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* desktop: table */}
          <Card className="hidden overflow-x-auto p-0 sm:block">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  {['Documento', 'Cliente', 'Categoría', 'Vence', 'Estado', 'Acciones'].map((h) => (
                    <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageDocs.map((d, i) => (
                  <tr
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/revision/${d.expId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/revision/${d.expId}`);
                      }
                    }}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                  >
                    <td className="px-5 py-3 font-bold">{d.name}</td>
                    <td className="px-5 py-3 text-ink-soft">{d.cliente}</td>
                    <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{d.category}</td>
                    <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{d.expiry ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Pill tone={docTone(d.estado)}>{d.estado}</Pill>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewing(d);
                          }}
                          aria-label="Ver documento"
                          title="Ver"
                          className="rounded-lg border border-line-strong bg-panel p-1.5 text-ink-soft transition-colors hover:border-ink-faint"
                        >
                          <Eye className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                        <button
                          onClick={(e) => void handleDownload(d, e)}
                          aria-label="Descargar documento"
                          title="Descargar"
                          className="rounded-lg border border-line-strong bg-panel p-1.5 text-ink-soft transition-colors hover:border-ink-faint"
                        >
                          <Download className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] text-ink-faint">
                {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, docs.length)} de {docs.length}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => p - 1)}
                  aria-label="Página anterior"
                  className="rounded-lg border border-line-strong p-1.5 text-ink-soft transition-colors hover:border-ink-faint disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={safePage >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Página siguiente"
                  className="rounded-lg border border-line-strong p-1.5 text-ink-soft transition-colors hover:border-ink-faint disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {previewing && (
        <DocumentPreviewDialog
          title={previewing.name}
          cacheKey={`${previewing.expId}:${previewing.id}`}
          fetchUrl={() => api.getDocumentDownloadUrl(previewing.expId, previewing.id)}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}
